/* WAYAK landing v3 — shared design tokens & content.
   Light · Wayak DNA (Arial, uppercase tracked, hard edges, corner-L, emblem)
   fused with technical glass. Strictly B&W. */
import type { CSSProperties } from "react";
import industriesData from "./industries.json";

/** Public asset base for the landing (logos, emblems, product screenshots). */
export const ASSET = "/landing-v3";

export const GLASS: CSSProperties = {
  background: "rgba(255,255,255,0.62)",
  backdropFilter: "blur(22px) saturate(1.2)",
  WebkitBackdropFilter: "blur(22px) saturate(1.2)",
  border: "1px solid rgba(0,0,0,0.10)",
  boxShadow:
    "0 12px 44px rgba(0,0,0,0.07), inset 0 1px 0 rgba(255,255,255,0.85)",
};

export const GLASS_SOLID: CSSProperties = {
  background: "rgba(255,255,255,0.78)",
  backdropFilter: "blur(16px) saturate(1.15)",
  WebkitBackdropFilter: "blur(16px) saturate(1.15)",
  border: "1px solid rgba(0,0,0,0.10)",
};

export const CLIENTS = [
  "GOOGLE",
  "XIGNUX",
  "ACB",
  "CASA LUMBRE",
  "ALMAVE",
  "CONTRALUZ",
  "GRAN MALO",
  "IQOM",
  "PLATANUS",
  "DESCIFRA",
];

/** Real client logo images (approved for use). Rendered grayscale +
   multiply-blended so mixed source files (transparent / white-bg / colored)
   all read as one standardized set. `h` is a per-logo height tuned so wide
   lockups and square marks sit at the same optical size. */
export const CLIENT_LOGOS: { name: string; src: string; h: number }[] = [
  { name: "Google for Startups", src: `${ASSET}/clients/google.png`, h: 24 },
  { name: "Xignux", src: `${ASSET}/clients/xignux.png`, h: 30 },
  { name: "ACB", src: `${ASSET}/clients/acb.png`, h: 32 },
  { name: "Casa Lumbre", src: `${ASSET}/clients/casa-lumbre.png`, h: 38 },
  { name: "Almave", src: `${ASSET}/clients/almave.png`, h: 24 },
  { name: "Contraluz", src: `${ASSET}/clients/contraluz.png`, h: 30 },
  { name: "Gran Malo", src: `${ASSET}/clients/gran-malo.png`, h: 40 },
  { name: "IQOM", src: `${ASSET}/clients/iqom.png`, h: 28 },
  { name: "Platanus", src: `${ASSET}/clients/platanus.png`, h: 22 },
  { name: "Descifra", src: `${ASSET}/clients/descifra.png`, h: 20 },
];

/** [name, description, slug] — slug drives /use-cases/[industry]. */
export const INDUSTRIES: [string, string, string][] = [
  [
    "Manufacturing",
    "Production cost, quality, shift performance, maintenance, material variance.",
    "manufacturing",
  ],
  [
    "Customer Service",
    "Email drafting, ticket classification, escalation analysis, SLA monitoring.",
    "customer-service",
  ],
  [
    "Insurance",
    "Claims intake, reserve estimation, fraud detection, coverage verification.",
    "insurance",
  ],
  [
    "HR",
    "Resume screening, onboarding, policy Q&A, scheduling, attrition analysis.",
    "hr",
  ],
  [
    "Retail",
    "Restock recommendations, promotion analysis, price elasticity, benchmarking.",
    "retail",
  ],
  [
    "Logistics",
    "Route efficiency, delivery exceptions, last-mile visibility, capacity planning.",
    "logistics",
  ],
  [
    "Finance & Banking",
    "Loan processing, portfolio risk, regulatory reporting, credit scoring.",
    "finance",
  ],
  [
    "Healthcare",
    "Patient intake, clinical documentation, compliance, medical coding.",
    "healthcare",
  ],
  [
    "Energy & Utilities",
    "Asset health, demand forecasting, outage management, compliance.",
    "energy",
  ],
  [
    "Real Estate",
    "Tenant screening, lease management, occupancy optimization, reporting.",
    "real-estate",
  ],
];

/** Real signup flow (wires the trial CTAs). */
export const SIGNUP_URL = "/auth/sign-up";
export const LOGIN_URL = "/auth/login";
export const DOCS_URL = "https://docs.wayak.io";
/** Demo booking (Google Calendar appointment page) + human contact (HubSpot form). */
export const DEMO_URL = "https://calendar.app.google/cjAVyovx4dSS3UsJ6";
export const CONTACT_URL = "https://s0noz.share.hsforms.com/2GWLXC5fySh20b7utDLgc9Q";

/* ---- Industry use-case data (server-safe; do NOT move into a "use client"
   module — server components import these helpers in generateStaticParams). */

export interface IndustryExample {
  title: string;
  problem: string;
  sources: string[];
  agent: string;
  playbook: string;
  result: string;
}

export interface IndustryData {
  slug: string;
  name: string;
  eyebrow: string;
  headline: string;
  sub: string;
  image: string;
  imageAlt: string;
  /** Hero stat strip: [value, label] x3. */
  stats: [string, string][];
  /** "Why this industry runs on Wayak": [title, description] x3. */
  benefits: [string, string][];
  automations: [string, string][];
  /** Concrete, worked use cases shown as detailed cards. */
  examples: IndustryExample[];
  featured: { use: string; goal: string; agent: string; playbook: string };
  sources: string[];
  outcome: [string, string];
  /** Industry-flavored testimonial (generic attribution). */
  quote: { text: string; role: string; org: string };
}

const INDUSTRY_MAP = industriesData as unknown as Record<string, IndustryData>;

export const INDUSTRY_SLUGS = Object.keys(INDUSTRY_MAP);
export const getIndustry = (slug: string): IndustryData | undefined =>
  INDUSTRY_MAP[slug];
export const getAllIndustries = (): IndustryData[] =>
  Object.values(INDUSTRY_MAP);
